import React, { useState, useEffect } from 'react';

// Icons
const CloseIcon = ({ size = 20, className = '' }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

// Configuration for Filter Screens (Same as UserFormModal)
const FILTER_CONFIG = {
    'ProjectList': { endpoint: '/project', key: 'No', labelField1: 'No', labelField2: 'Description' },
    'Feeder': { endpoint: '/feeder', key: 'Code', labelField1: 'Code', labelField2: 'Description' },
    'WAREHOUSE': { endpoint: '/locations', key: 'LocationCode', labelField1: 'LocationCode', labelField2: 'LocationName' },
    'Branch': { endpoint: '/branches', key: 'BranchCode', labelField1: 'BranchCode', labelField2: 'BranchName' }
};

const UserViewModal = ({ open, onClose, user }) => {
    // --- STATE ---
    const [activeTab, setActiveTab] = useState('userInfo');
    const [isMaximized, setIsMaximized] = useState(false);
    
    // Data states
    const [screens, setScreens] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [permissionScreenList, setPermissionScreenList] = useState([]);
    const [profileDefaults, setProfileDefaults] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- NEW: Filter States ---
    const [screenFilters, setScreenFilters] = useState([]);
    const [filterOptions, setFilterOptions] = useState({});
    
    // Helper for Auth Headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    // --- FETCHING LOGIC ---

    const fetchScreens = async () => {
        try {
            const response = await fetch(`${API_URL}/screen-name`, { headers: getAuthHeaders() });
            const data = await response.json();
            setScreens(data);
            return data; 
        } catch (error) {
            console.error('Error fetching screens:', error);
            return [];
        }
    };

    const fetchProfiles = async () => {
        try {
            const response = await fetch(`${API_URL}/role-profiles`, { headers: getAuthHeaders() });
            const data = await response.json();
            setProfiles(data);
        } catch (error) {
            console.error('Error fetching profiles:', error);
        }
    };

    const fetchUserOverrides = async (userId) => {
        try {
            const response = await fetch(`${API_URL}/user/${userId}/permissions`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                return data.permissions || [];
            }
        } catch (error) {
            console.error('Error fetching user overrides:', error);
        }
        return [];
    };

    const fetchProfilePermissions = async (profileID) => {
        if (!profileID) return [];
        try {
            const response = await fetch(`${API_URL}/profile-permissions/${profileID}`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                return data.permissions || data || [];
            }
        } catch (error) {
            console.error('Error fetching profile permissions:', error);
        }
        return [];
    };

    // --- NEW: Fetch Filter Options ---
    const fetchFilterData = async (screenName) => {
        const config = FILTER_CONFIG[screenName];
        if (!config) return;
        
        if (filterOptions[screenName]) return; // Cache check

        try {
            const response = await fetch(`${API_URL}${config.endpoint}`, { headers: getAuthHeaders() });
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data.screens || data.data || []);
            
            setFilterOptions(prev => ({ ...prev, [screenName]: list }));
        } catch (error) {
            console.error(`Error fetching ${screenName}:`, error);
        }
    };

    // Load logic for Permissions Tab
       // Load logic for Permissions Tab
    const loadPermissionsForView = async () => {
        if (!user || !user.UserID) return;

        setLoading(true);
        try {
            const screenList = await fetchScreens(); 
            await fetchProfiles();
            
            // 1. Fetch User Overrides
            const userOverrides = await fetchUserOverrides(user.UserID);

            // 2. Fetch Profile Defaults
            let profileDefaultsRaw = [];
            if (user.ProfileID) {
                profileDefaultsRaw = await fetchProfilePermissions(user.ProfileID);
            }
            
            // Save raw profile defaults for the "Profile Default" column indicator
            setProfileDefaults(profileDefaultsRaw);

            // 3. Merge Logic
            const mergedScreens = screenList.map(screen => {
                const base = {
                    ScreenID: screen.ScreenID,
                    ScreenName: screen.ScreenName,
                    ScreenCode: screen.ScreenCode,
                    ScreenAccess: false,
                    CanView: false,
                    CanCreate: false,
                    CanEdit: false,
                    CanDelete: false
                };

                // Layer 1: Apply Profile Defaults
                const profilePerm = profileDefaultsRaw.find(pp => pp.ScreenID === screen.ScreenID);
                // Ensure we don't let DB NULLs overwrite our 'false' defaults here either
                const withProfile = { 
                    ...base, 
                    ...(profilePerm || {}),
                    // Explicitly normalize profile perms to boolean (fixes NULL issues)
                    ScreenAccess: profilePerm?.ScreenAccess === true,
                    CanView: profilePerm?.CanView === true,
                    CanCreate: profilePerm?.CanCreate === true,
                    CanEdit: profilePerm?.CanEdit === true,
                    CanDelete: profilePerm?.CanDelete === true
                };

                // Layer 2: Apply User Overrides
                const userPerm = userOverrides.find(up => up.ScreenID === screen.ScreenID);
                
                if (userPerm) {
                    return {
                        ...withProfile,
                        // FIX: Check for both null AND undefined.
                        // If the user override is missing or null, use the Profile value (withProfile).
                        ScreenAccess: (userPerm.ScreenAccess !== null && userPerm.ScreenAccess !== undefined) 
                                        ? userPerm.ScreenAccess : withProfile.ScreenAccess,
                        
                        CanView: (userPerm.CanView !== null && userPerm.CanView !== undefined) 
                                    ? userPerm.CanView : withProfile.CanView,
                        
                        CanCreate: (userPerm.CanCreate !== null && userPerm.CanCreate !== undefined) 
                                    ? userPerm.CanCreate : withProfile.CanCreate,
                        
                        CanEdit: (userPerm.CanEdit !== null && userPerm.CanEdit !== undefined) 
                                    ? userPerm.CanEdit : withProfile.CanEdit,
                        
                        CanDelete: (userPerm.CanDelete !== null && userPerm.CanDelete !== undefined) 
                                    ? userPerm.CanDelete : withProfile.CanDelete,
                    };
                }

                return withProfile;
            });

            setPermissionScreenList(mergedScreens);
        } catch (error) {
            console.error('Error loading view permissions:', error);
        } finally {
            setLoading(false);
        }
    };

    // --- NEW: Load User Details & Filters ---
    const loadUserDetails = async () => {
        if (!user || !user.UserID) return;

        try {
            // Fetch full user details to get screenFilters
            const response = await fetch(`${API_URL}/user/${user.UserID}`, { headers: getAuthHeaders() });
            const userData = await response.json();

            if (userData.screenFilters && Array.isArray(userData.screenFilters)) {
                setScreenFilters(userData.screenFilters);
                
                // Pre-fetch options for dropdowns so we can show names
                userData.screenFilters.forEach(f => {
                    if(f.ScreenName) fetchFilterData(f.ScreenName);
                });
            } else {
                setScreenFilters([]);
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    // --- EFFECTS ---
    useEffect(() => {
        if (!user || !open) return;

        fetchProfiles();
        loadUserDetails(); // Load user details (filters)

        if (user.Role === 'Admin') {
            setActiveTab('userInfo');
        } else {
            if (activeTab === 'permissions') {
                loadPermissionsForView();
            }
        }
    }, [open, user, activeTab]);

    const toggleMaximize = () => setIsMaximized(!isMaximized);

    const selectedProfile = profiles.find(p => p && p.ProfileID === parseInt(user?.ProfileID));
    const selectedProfileName = selectedProfile ? `${selectedProfile.ProfileCode || ''} - ${selectedProfile.ProfileName || ''}` : (user?.ProfileID ? 'Unknown Profile' : 'No Profile Assigned');

    const isOverridden = (screenID, field, currentValue) => {
        const profilePerm = profileDefaults.find(p => p.ScreenID === screenID);
        if (!profilePerm) return false; 
        const profileValue = profilePerm[field];
        return (profileValue !== currentValue);
    };

    // Define Tabs
    const tabs = [
        { id: 'userInfo', name: 'User Info' },
        { id: 'permissions', name: 'Permissions' },
        { id: 'filters', name: 'Data Filters' } // NEW TAB
    ];

    // Filter out permissions & filters tab if user is Admin
    const visibleTabs = user?.Role === 'Admin' 
        ? tabs.filter(t => t.id !== 'permissions' && t.id !== 'filters') 
        : tabs;

    // --- RENDER GUARD ---
    if (!user) return null;

    return (
        <>
            <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <style>{`
                    .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    .hide-scrollbar::-webkit-scrollbar { display: none; }
                `}</style>
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
                <div className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="flex-shrink-0 p-8 pb-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-[#384959]">View User Details</h2>
                            <div className="flex items-center gap-3">
                                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors" onClick={toggleMaximize}>
                                    {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
                                </button>
                                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors" onClick={onClose}>
                                    <CloseIcon size={24} />
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex-shrink-0 px-8 pb-6">
                        <div className="flex border-b border-[#BDDDFC] -mx-8 px-8 overflow-x-auto overflow-y-hidden whitespace-nowrap">
                            {visibleTabs.map(tab => (
                                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-4 px-4 -mb-px text-sm font-medium transition-colors focus:outline-none ${activeTab === tab.id ? 'border-b-2 border-[#6A89A7] text-[#6A89A7]' : 'border-b-2 border-transparent text-[#A0AEC0] hover:text-[#6A89A7]'}`}>
                                    {tab.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
                        <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0] min-h-full">
                            
                            {/* USER INFO TAB */}
                            {activeTab === 'userInfo' && (
                                <div className='space-y-6'>
                                    <h3 className="text-lg font-semibold text-[#384959] mb-4 border-b border-gray-200 pb-2">User Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Username</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 font-medium uppercase">
                                                {user.Username || '-'}
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Full Name</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 font-medium uppercase">
                                                {user.FullName || '-'}
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Email</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                                                {user.Email || '-'}
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Phone</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 uppercase">
                                                {user.Phone || '-'}
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Role</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                                                {user.Role || '-'}
                                            </div>
                                        </div>
                                        
                                        {user.Role !== 'Admin' && (
                                            <div className='flex flex-col gap-2'>
                                                <label className="block text-sm font-medium text-gray-500">Profile</label>
                                                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                                                    {selectedProfileName}
                                                </div>
                                            </div>
                                        )}

                                        {(user.DepartmentName || user.DepartmentID) && (
                                            <div className='flex flex-col gap-2'>
                                                <label className="block text-sm font-medium text-gray-500">Department</label>
                                                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 uppercase">
                                                    {user.DepartmentName ? `${user.DepartmentCode || ''} - ${user.DepartmentName}` : user.DepartmentID || '-'}
                                                </div>
                                            </div>
                                        )}
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Status</label>
                                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm uppercase w-fit ${user.IsActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                                {user.IsActive ? "Active" : "Inactive"}
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-2'>
                                            <label className="block text-sm font-medium text-gray-500">Created At</label>
                                            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                                                {formatDate(user.CreatedAt)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PERMISSIONS TAB */}
                            {activeTab === 'permissions' && (
                                <div>
                                    <div className="mb-4">
                                        <h3 className="text-lg font-semibold text-[#384959]">Screen Access Configuration</h3>
                                        <p className="text-sm text-gray-500">
                                            Viewing permissions for "<strong>{selectedProfileName}</strong>".
                                        </p>
                                    </div>
                                    
                                    <div className="mb-4 bg-blue-50 text-blue-800 px-4 py-2 rounded border border-blue-100 text-sm">
                                        <span className="font-bold">Blue checkboxes</span> indicate User Overrides.
                                        <span className="font-bold text-gray-600 ml-2">Gray checkboxes</span> indicate Profile Defaults.
                                    </div>

                                    {loading ? <div className="text-center py-8">Loading permissions...</div> : (
                                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                            <table className="min-w-full divide-y divide-gray-200 bg-white">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Screen Name</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Profile Default</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">User Access</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">View</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Create</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Delete</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {permissionScreenList.map((screen) => {
                                                        const profilePerm = profileDefaults.find(p => p.ScreenID === screen.ScreenID);
                                                        const profileAccess = profilePerm ? profilePerm.ScreenAccess : false;
                                                        
                                                        return (
                                                            <tr key={screen.ScreenID} className="hover:bg-gray-50">
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                    {screen.ScreenName}
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                                                    <div className={`inline-flex justify-center items-center w-5 h-5 rounded border ${profileAccess ? 'bg-green-100 border-green-300 text-green-600' : 'bg-gray-100 border-gray-300 text-gray-400'}`}>
                                                                        {profileAccess ? '✓' : '-'}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                                                    <input type="checkbox" checked={screen.ScreenAccess} disabled className={`h-4 w-4 cursor-not-allowed ${isOverridden(screen.ScreenID, 'ScreenAccess', screen.ScreenAccess) ? 'checkbox-overridden' : 'checkbox-inherited'}`} />
                                                                </td>
                                                                {['CanView', 'CanCreate', 'CanEdit', 'CanDelete'].map(perm => (
                                                                    <td key={perm} className="px-4 py-3 whitespace-nowrap text-center">
                                                                        <input type="checkbox" checked={screen[perm]} disabled={!screen.ScreenAccess} className={`h-4 w-4 cursor-not-allowed ${isOverridden(screen.ScreenID, perm, screen[perm]) ? 'checkbox-overridden' : 'checkbox-inherited'}`} />
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    })}
                                                    {permissionScreenList.length === 0 && !loading && (
                                                        <tr>
                                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                                                No permissions data available.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- NEW: FILTERS TAB --- */}
                            {activeTab === 'filters' && (
                                <div className="bg-white rounded-xl p-6 border border-gray-200">
                                    <div className="mb-4">
                                        <h3 className="text-lg font-semibold text-[#384959]">Data Access Filters</h3>
                                        <p className="text-sm text-gray-500">User is restricted to the following data.</p>
                                    </div>

                                    {screenFilters.length === 0 ? (
                                        <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                                            No filters applied. User has access to all data based on permissions.
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {screenFilters.map((filter, index) => {
                                                const config = FILTER_CONFIG[filter.ScreenName];
                                                const options = filterOptions[filter.ScreenName] || [];
                                                
                                                return (
                                                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                                        <div className="flex justify-between items-center mb-3 border-b pb-2">
                                                            <h4 className="font-semibold text-gray-700">{filter.ScreenName === 'WAREHOUSE' ? 'Location' : filter.ScreenName}</h4>
                                                            <span className="text-xs text-gray-400">{filter.Values?.length || 0} items</span>
                                                        </div>
                                                        
                                                        <div className="flex flex-wrap gap-2">
                                                            {filter.Values && filter.Values.length > 0 ? (
                                                                filter.Values.map(val => {
                                                                    // Find readable name for the ID
                                                                    const item = options.find(opt => opt[config?.key] === val);
                                                                    const displayLabel = item 
                                                                        ? `${item[config.labelField1]} - ${item[config.labelField2]}` 
                                                                        : val;
                                                                    
                                                                    return (
                                                                        <span 
                                                                            key={val} 
                                                                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                                                                        >
                                                                            {displayLabel}
                                                                        </span>
                                                                    );
                                                                })
                                                            ) : (
                                                                <span className="text-gray-400 text-sm">No values selected</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserViewModal;